<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button variant="primary" class="mr-1" size="xs" @click="toggle"
        ><i class="fas fa-edit"></i>&nbsp;Edit</b-button
      >
    </slot>

    <b-modal
      @show="onModalShow"
      @hidden="resetModal"
      ref="modal-container"
      v-model="modalShow"
      size="lg"
      :title="title"
      :static="true"
      :lazy="true"
      hide-footer
      :no-close-on-backdrop="submitting"
      :no-close-on-esc="submitting"
      :hide-header-close="submitting"
    >
      <form ref="form" @submit.stop.prevent="handleSubmit">
        <fieldset :disabled="submitting">
        <b-form-group v-if="!this.create" label="_ID" label-for="id-input">
          <b-form-input
            id="id-input"
            v-model="formValues.id"
            autocomplete="off"
            disabled
          />
        </b-form-group>

        <b-row>
          <b-col>
            <b-form-group label="Template" label-for="template-input">
              <multiselect
                name="template-input"
                v-model="selectedTemplate"
                :options="templateOptions"
                :searchable="true"
                :internal-search="true"
                label="text"
                track-by="value"
                deselect-label=""
                select-label="Select"
                placeholder="Select template"
                :disabled="submitting"
              >
              </multiselect>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <b-form-group label="Limit Type" label-for="limitType-input">
              <multiselect
                name="limitType-input"
                v-model="selectedLimitType"
                :options="limitTypeOptions"
                :searchable="true"
                :internal-search="true"
                label="text"
                track-by="value"
                deselect-label=""
                select-label="Select"
                placeholder="Select limit type"
                :disabled="submitting || !this.create"
              >
              </multiselect>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col v-if="!this.isCreateFromTemplate">
            <b-form-group label="Game Type" label-for="gameType-input">
              <multiselect
                name="gameType-input"
                v-model="selectedGameType"
                :options="typeOptions"
                :searchable="true"
                :internal-search="true"
                label="text"
                track-by="value"
                deselect-label=""
                select-label="Select"
                placeholder="Select game type"
                :disabled="submitting || !this.create || this.isCreateFromTemplate"
                v-validate="{ required: this.create }"
                :state="validateState('gameType-input')"
                aria-describedby="gameType-input-live-feedback"
                data-vv-name="gameType-input"
                data-vv-as="Game Type"
                :required="this.create"
              />
              <b-form-invalid-feedback id="gameType-input-live-feedback">{{
                veeErrors.first('gameType-input')
              }}</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="Operator" label-for="operator-input">
              <multiselect
                name="operator-input"
                v-model="selectedOperator"
                :options="operatorOptions"
                :searchable="true"
                :internal-search="true"
                label="text"
                track-by="value"
                deselect-label=""
                select-label="Select"
                placeholder="Select operator"
                :disabled="submitting || !this.create"
                v-validate="this.create ? { required: true } : {}"
                :state="validateState('operator-input')"
                aria-describedby="operator-input-live-feedback"
                data-vv-name="operator-input"
                data-vv-as="Operator"
                :required="this.create"
              />

          <b-form-invalid-feedback id="operator-input-live-feedback">{{
                veeErrors.first('operator-input')
              }}</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="!this.isCreateFromTemplate">
          <b-col>
            <b-form-group label="Table ID" label-for="tableId-input">
              <multiselect
                name="tableId-input"
                v-model="selectedTable"
                :options="tableOptions"
                :searchable="true"
                :internal-search="true"
                label="text"
                track-by="value"
                deselect-label=""
                select-label="Select"
                placeholder="Select table"
                :disabled="submitting || !this.create"
                v-validate="this.create ? { required: true } : {}"
                :state="validateState('tableId-input')"
                aria-describedby="tableId-input-live-feedback"
                data-vv-name="tableId-input"
                data-vv-as="Table ID"
                :required="this.create"
              />
              <b-form-invalid-feedback id="tableId-input-live-feedback">{{
                veeErrors.first('tableId-input')
              }}</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="Currency" label-for="currency-input">
              <multiselect
                name="currency-input"
                v-model="selectedCurrency"
                :options="currencyOptions"
                :searchable="true"
                :internal-search="true"
                label="text"
                track-by="value"
                deselect-label=""
                select-label="Select"
                placeholder="Select currency"
                :disabled="submitting || !this.create"
                v-validate="this.create ? { required: true } : {}"
                :state="validateState('currency-input')"
                aria-describedby="currency-input-live-feedback"
                data-vv-name="currency-input"
                data-vv-as="Currency"
                :required="this.create"
              />
              <b-form-invalid-feedback id="currency-input-live-feedback">{{
                veeErrors.first('currency-input')
              }}</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <section v-if="!isCreateFromTemplate" class="border-top pt-3">
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Calculation mode" :label-for="'calculation-mode-' + _uid">
                <b-form-select :id="'calculation-mode-' + _uid" v-model="formValues.calculation.mode" :options="calculationModes" />
              </b-form-group>
            </b-col>
            <b-col v-if="!isEurSource" cols="12" md="6">
              <b-form-group label="EUR source" :label-for="'eur-source-' + _uid">
                <b-form-select :id="'eur-source-' + _uid" v-model="formValues.calculation.eurSourceId" :options="eurSourceOptions" :disabled="sourcesLoading || !sourceScopeReady" />
                <small v-if="sourcesLoading" class="text-muted" role="status">Loading EUR sources...</small>
                <small v-else-if="sourceScopeReady && !eurSources.length && !sourcesError" class="text-warning">No EUR source for this table, operator and limit type</small>
              </b-form-group>
            </b-col>
          </b-row>
          <b-alert v-if="sourcesError" show variant="danger">
            {{ sourcesError }}
            <b-button type="button" size="sm" variant="outline-danger" @click="loadEurSources"><i class="fas fa-sync" aria-hidden="true"></i> Retry</b-button>
          </b-alert>
          <b-alert v-if="missingSelectedSource" show variant="warning">Selected EUR source is unavailable for this table, operator and limit type.</b-alert>
          <b-tabs content-class="mt-3">
            <b-tab title="Draft" active>
              <b-form-radio-group v-model="profile" :options="profileOptions" buttons button-variant="outline-primary" class="mb-3" aria-label="Draft profile" />
              <template v-if="profile === 'peak'">
                <template v-if="isEurSource">
                  <b-form-checkbox :checked="!!formValues.calculation.peakSettings" :disabled="settingsLoading || !formValues.settings" class="mb-3" @change="togglePeak">Peak enabled</b-form-checkbox>
                  <template v-if="formValues.calculation.peakSettings">
                    <b-form-group label="Peak timezone" :label-for="'peak-timezone-' + _uid">
                      <b-form-input :id="'peak-timezone-' + _uid" v-model.trim="formValues.calculation.peakSchedule.timezone" placeholder="Europe/Kyiv" autocomplete="off" required />
                    </b-form-group>
                    <div v-for="(window, index) in formValues.calculation.peakSchedule.windows" :key="index" class="border-bottom pb-2 mb-3">
                      <b-form-group :label="'Window ' + (index + 1)">
                        <b-form-checkbox-group v-model="window.days" :options="weekdays" :aria-label="'Window ' + (index + 1) + ' days'" />
                      </b-form-group>
                      <b-row class="align-items-end">
                        <b-col cols="5"><b-form-group label="Start" :label-for="'peak-start-' + _uid + '-' + index"><b-form-input :id="'peak-start-' + _uid + '-' + index" v-model="window.start" type="time" required /></b-form-group></b-col>
                        <b-col cols="5"><b-form-group label="End" :label-for="'peak-end-' + _uid + '-' + index"><b-form-input :id="'peak-end-' + _uid + '-' + index" v-model="window.end" type="time" required /></b-form-group></b-col>
                        <b-col cols="2" class="mb-3"><b-button type="button" variant="outline-danger" :aria-label="'Remove window ' + (index + 1)" title="Remove window" @click="formValues.calculation.peakSchedule.windows.splice(index, 1)"><i class="fas fa-trash" aria-hidden="true"></i></b-button></b-col>
                      </b-row>
                    </div>
                    <b-button type="button" variant="outline-primary" size="sm" class="mb-3" :disabled="formValues.calculation.peakSchedule.windows.length >= 28" @click="addPeakWindow"><i class="fas fa-plus" aria-hidden="true"></i> Add window</b-button>
                  </template>
                </template>
                <template v-else>
                  <h6>EUR source draft peak</h6>
                  <p class="text-muted source-id">{{ effectiveEurSource ? effectiveEurSource.id : 'No matching EUR source' }}</p>
                  <LimitSettingsPreview :settings="sourcePeakSettings" empty-text="No EUR source peak settings" />
                  <LimitSettingsPreview v-if="sourcePeakSchedule" :settings="sourcePeakSchedule" />
                </template>
              </template>
        <b-tabs
          content-class="mt-3"
          :key="profile"
          v-if="activeSettings && (profile === 'normal' || isEurSource)"
        >
          <div class="row" v-if="settingsFromTemplate">
            <div class="col">
              <div class="alert alert-info" role="alert">
                Limits are edited in the template. Unselect template to edit
                limits manually
              </div>
            </div>
          </div>
          <b-tab
            :title="prepareLabel(tab)"
            v-for="tab in regularSettingsTabs(activeSettings)"
            v-bind:key="tab"
          >
            <b-row v-if="activeSettings[tab] && activeSettings[tab].constructor === Object">
              <b-col
                cols="6"
                v-for="key in scalarKeys(activeSettings[tab])"
                v-bind:key="key"
              >
                <b-form-group
                  :label="prepareLabel(key)"
                  :label-for="profile + '-' + tab + '-' + key + '-' + _uid"
                >
                  <b-form-input
                    :id="profile + '-' + tab + '-' + key + '-' + _uid"
                    v-model="activeSettings[tab][key]"
                    :state="null"
                    autocomplete="off"
                    required
                    :disabled="settingsLoading || settingsFromTemplate"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="Array.isArray(activeSettings[tab])">
              <b-col
                cols="12"
                v-for="(chip, index) in activeSettings[tab]"
                v-bind:key="index"
              >
                <h5>Chip {{ index + 1 }}</h5>
                <b-row>
                  <b-col
                    cols="4"
                    v-for="key in Object.keys(chip)"
                    v-bind:key="key"
                    :class="{ 'd-none': key === 'position' }"
                  >
                    <b-form-group
                      :label="prepareLabel(key)"
                      :label-for="profile + '-' + tab + '-' + index + '-' + key + '-' + _uid"
                    >
                      <b-form-input
                        :id="profile + '-' + tab + '-' + index + '-' + key + '-' + _uid"
                        v-model="activeSettings[tab][index][key]"
                        :state="null"
                        autocomplete="off"
                        required
                        :disabled="settingsLoading || settingsFromTemplate"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-button
              variant="primary"
              type="button"
              :disabled="settingsLoading || settingsFromTemplate"
              v-if="Array.isArray(activeSettings[tab])"
              @click="addSetting(tab)"
              ><i class="fas fa-plus" aria-hidden="true"></i> Add</b-button
            >
          </b-tab>

          <b-tab title="Bonus chips">
            <BonusLimitsEditor :settings="activeSettings" :disabled="submitting || settingsLoading || settingsFromTemplate" />
          </b-tab>

          <b-tab title="Bingo" v-if="hasBingoLimits">
            <b-form-checkbox :checked="bingoEnabled" :disabled="settingsLoading || settingsFromTemplate" @change="setBingoEnabled">Enabled</b-form-checkbox>
            <b-row v-if="bingoSideBet" class="mt-3">
              <b-col cols="6">
                <b-form-group label="Bingo Min" :label-for="'bingo-min-' + profile + '-' + _uid">
                  <b-form-input :id="'bingo-min-' + profile + '-' + _uid" v-model="bingoSideBet.min" type="number" min="0" step="any" autocomplete="off" :disabled="settingsLoading || settingsFromTemplate || !bingoEnabled" />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="Bingo Max" :label-for="'bingo-max-' + profile + '-' + _uid">
                  <b-form-input :id="'bingo-max-' + profile + '-' + _uid" v-model="bingoSideBet.max" type="number" min="0" step="any" autocomplete="off" :disabled="settingsLoading || settingsFromTemplate || !bingoEnabled" />
                </b-form-group>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="Side Bets" v-if="hasSideBets">
            <h5 class="mb-3">Side Bets ({{ sideBets.length }}/{{ maxSideBets }})</h5>
            <div
              v-for="(sidebet, sidebetIndex) in sideBets"
              v-bind:key="'sidebet-cell-' + sidebetIndex"
              class="p-2 mb-3"
              style="border: 1px solid #ddd; border-radius: 4px"
            >
              <b-row>
                <b-col
                  cols="12"
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <strong>Order {{ sidebet.order }}</strong>
                  <b-button
                    v-if="!settingsFromTemplate"
                    variant="danger"
                    size="sm"
                    @click="removeSidebet(sidebet)"
                    >Remove</b-button
                  >
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="6">
                  <b-form-group
                    label="Type"
                    :label-for="'sidebet-type-' + sidebet.order"
                  >
                    <multiselect
                      :name="'sidebet-type-' + sidebet.order"
                      :value="typeOptionFor(sidebet.type)"
                      :options="availableTypeOptions(sidebet)"
                      :searchable="true"
                      :allow-empty="false"
                      label="text"
                      track-by="value"
                      deselect-label=""
                      select-label="Select"
                      placeholder="Select type"
                      :disabled="submitting || settingsLoading || settingsFromTemplate"
                      @input="option => onTypeSelect(sidebet, option)"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="6" class="d-flex align-items-center">
                  <b-form-checkbox
                    v-model="sidebet.enabled"
                    :disabled="settingsLoading || settingsFromTemplate"
                    >Enabled</b-form-checkbox
                  >
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="6">
                  <b-form-group
                    label="Min"
                    :label-for="'sidebet-min-' + sidebet.order"
                  >
                    <b-form-input
                      :id="'sidebet-min-' + sidebet.order"
                      v-model="sidebet.min"
                      type="number"
                      autocomplete="off"
                      required
                      :disabled="settingsLoading || settingsFromTemplate"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group
                    label="Max"
                    :label-for="'sidebet-max-' + sidebet.order"
                  >
                    <b-form-input
                      :id="'sidebet-max-' + sidebet.order"
                      v-model="sidebet.max"
                      type="number"
                      autocomplete="off"
                      required
                      :disabled="settingsLoading || settingsFromTemplate"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12">
                  <small class="text-muted">
                    key: {{ sidebet.key || '—' }} · title:
                    {{ sidebet.title || '—' }} · calculator:
                    {{ sidebet.calculator || '—' }} · side:
                    {{ sidebet.side || '—' }}
                    <span v-if="sidebet.payout"> · payout: {{ sidebet.payout }}</span>
                  </small>
                </b-col>
              </b-row>
            </div>

            <b-button
              v-if="sideBets.length < maxSideBets && !settingsFromTemplate"
              variant="primary"
              @click="addSidebet"
              >Add Side Bet</b-button
            >
          </b-tab>
        </b-tabs>
            </b-tab>
            <b-tab title="Published">
              <PublishedLimits :published="create ? null : formValues.published" />
            </b-tab>
          </b-tabs>
        </section>

        <b-form-group>
          <v-jsoneditor
            hidden
            ref="settings-editor"
            v-model="formValues.settings"
            :options="{
              mode: 'code',
              enableSort: false,
              enableTransform: false
            }"
            :plus="false"
          />
          <b-form-input
            id="settings-editor-error"
            v-model="settingsEditorError"
            v-validate="{ is: '' }"
            :state="validateState('settings-editor-error')"
            aria-describedby="settings-editor-error-live-feedback"
            data-vv-name="settings-editor-error"
            data-vv-as="Value"
            hidden
            autocomplete="off"
          />
          <b-form-invalid-feedback
            id="settings-editor-error-live-feedback"
            v-if="veeErrors.first('settings-editor-error')"
          >
            {{ settingsEditorError }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-alert v-if="calculationError" show variant="danger" role="alert">{{ calculationError }}</b-alert>
        <b-alert v-if="bingoError" show variant="danger" role="alert">{{ bingoError }}</b-alert>
        <b-alert v-if="submitError" show variant="danger" role="alert">{{ submitError }}</b-alert>
        </fieldset>
      </form>

      <b-button
        class="mt-2 float-right"
        variant="primary"
        @click="handleSubmit"
        :disabled="!canSubmit"
        ><i v-if="submitting" class="fas fa-spinner fa-spin" aria-hidden="true"></i> {{ submitting ? 'Saving...' : 'Save draft' }}</b-button
      >
    </b-modal>
  </span>
</template>

<script>
import Multiselect from 'vue-multiselect'
import VJsoneditor from 'v-jsoneditor/src/index'
import Vue from 'vue'
import PublishedLimits from './PublishedLimits.vue'
import LimitSettingsPreview from './LimitSettingsPreview.vue'
import BonusLimitsEditor from './BonusLimitsEditor.vue'
import { bonusSettingsError, regularSettingsTabs } from './bonus-settings'
import { BINGO_SIDE_BET_DEFAULT, BINGO_SIDE_BET_TYPE } from '../../constants/BingoSideBet'
import { isRoulette } from '../../definitions/gameTypeIds'
import { LIMIT_TYPES } from '../../constants/LimitTypes'
import {
  SIDE_BET_TYPES,
  SIDE_BET_GAME_TYPE
} from '../../constants/SideBetTypes'

const prepareLabel = val => {
  return val
    .split(/_/g)
    .map(word => {
      return word[0].toUpperCase() + word.substr(1)
    })
    .join(' ')
}

export default {
  data() {
    return {
      modalShow: false,
      defaultSettings: null,
      formValues: this.getInitialValues(),
      settingsEditorError: '',
      settingsLoading: false,
      submitting: false,
      submitError: '',
      calculationError: '',
      bingoError: '',
      profile: 'normal',
      profileOptions: [{ text: 'Normal', value: 'normal' }, { text: 'Peak', value: 'peak' }],
      calculationModes: [{ value: 'inherit', text: 'Inherit currency mode' }, { value: 'auto', text: 'Auto' }, { value: 'manual', text: 'Manual' }],
      weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((text, index) => ({ text, value: index + 1 })),
      eurSources: [],
      sourcesLoading: false,
      sourcesError: '',
      sourceRequest: 0,
      settingsRequest: 0
    }
  },
  components: {
    Multiselect,
    VJsoneditor,
    PublishedLimits,
    LimitSettingsPreview,
    BonusLimitsEditor
  },
  props: {
    create: Boolean,
    title: String,
    defaultValues: Object
  },
  methods: {
    regularSettingsTabs,
    addSetting(tab) {
      const list = this.activeSettings[tab]
      const setting = { ...(list[0] || { nominal: '', color: '', position: 0 }) }
      Object.keys(setting).forEach(key => { setting[key] = '' })
      if (tab === 'chips') {
        setting.position = list.reduce((max, chip) => Math.max(max, Number(chip.position) || 0), -1) + 1
      }
      list.push(setting)
    },
    togglePeak(enabled) {
      if (!this.isEurSource) return
      this.formValues.calculation.peakSettings = enabled ? this.deepClone(this.formValues.settings) : null
      if (!enabled) this.formValues.calculation.peakSchedule = { timezone: 'UTC', windows: [] }
    },
    addPeakWindow() {
      if (this.formValues.calculation.peakSchedule.windows.length >= 28) return
      this.formValues.calculation.peakSchedule.windows.push({ days: [1, 2, 3, 4, 5], start: '18:00', end: '23:00' })
    },
    validateCalculation() {
      if (this.isCreateFromTemplate) return ''
      if (!this.calculationModes.some(option => option.value === this.formValues.calculation.mode)) return 'Select a calculation mode.'
      if (this.missingSelectedSource) return 'Select an available EUR source or use the matched EUR source.'
      if (!this.isEurSource || !this.formValues.calculation.peakSettings) return ''
      const peak = this.formValues.calculation.peakSettings
      if (!peak.limit || !Array.isArray(peak.chips) || !peak.chips.length) return 'Peak settings require limits and at least one chip.'
      if (peak.chips.some(chip => !Number.isFinite(Number(chip.nominal)) || Number(chip.nominal) <= 0)) return 'Peak chip denominations must be positive numbers.'
      const schedule = this.formValues.calculation.peakSchedule
      if (schedule.windows.length > 28) return 'Peak schedule supports at most 28 windows.'
      try {
        if (!schedule.timezone) throw new Error('Missing timezone')
        new Intl.DateTimeFormat('en', { timeZone: schedule.timezone }).format()
      } catch {
        return 'Peak timezone must be a valid IANA timezone.'
      }
      for (const [index, window] of schedule.windows.entries()) {
        if (!window.days.length || window.days.some(day => !Number.isInteger(day) || day < 1 || day > 7)) return `Window ${index + 1}: select at least one day.`
        if (![window.start, window.end].every(time => /^([01]\d|2[0-3]):[0-5]\d$/.test(time)) || window.start === window.end) return `Window ${index + 1}: enter different valid start and end times.`
      }
      return ''
    },
    async loadEurSources() {
      const request = ++this.sourceRequest
      this.eurSources = []
      this.sourcesError = ''
      this.sourcesLoading = false
      if (!this.modalShow || this.isEurSource || !this.sourceScopeReady || this.isCreateFromTemplate) return
      this.sourcesLoading = true
      const params = { tableId: this.formValues.tableId, partnerId: this.formValues.partnerId, currency: 'EUR', limitType: this.formValues.limitType, limit: 100, offset: 0 }
      try {
        const sources = []
        let total = 0
        do {
          const { data } = await this.$http({ url: '/api/v1/limits', method: 'GET', params: { ...params } })
          if (request !== this.sourceRequest) return
          const items = data.items || []
          sources.push(...items.filter(item => item.currency === 'EUR' && String(item.tableId) === String(params.tableId) && item.partnerId === params.partnerId && Number(item.limitType || 0) === Number(params.limitType)))
          total = Number(data.pagination?.totalCount || 0)
          params.offset += items.length
          if (!items.length) break
        } while (params.offset < total)
        this.eurSources = sources
      } catch (err) {
        if (request === this.sourceRequest) this.sourcesError = err.response?.data?.message || 'Could not load EUR sources.'
      } finally {
        if (request === this.sourceRequest) this.sourcesLoading = false
      }
    },
    getInitialValues() {
      return {
        id: null,
        type: null,
        partnerId: null,
        tableId: null,
        currency: null,
        settings: null,
        templateId: null,
        limitType: 0,
        limitName: 'Default',
        gameType: null,
        published: null,
        calculation: { mode: 'inherit', eurSourceId: null, peakSettings: null, peakSchedule: { timezone: 'UTC', windows: [] } }
      }
    },
    loadDefaultSettings(gameType) {
      const request = ++this.settingsRequest
      this.settingsLoading = true

      this.$store
        .dispatch('limits/loadDefaultSettings', gameType)
        .then(() => {
          if (request !== this.settingsRequest) return
          this.settingsEditorError = ''
          this.settingsLoading = false
          const limitType = this.$store.getters['limits/defaultSettings'](gameType)
          this.formValues.settings = this.deepClone(limitType?.settings ?? null)
          this.formValues.gameType = Number(limitType?.gameType?.[0] ?? null)
          this.normalizeSideBetSettings()
          this.refreshSideBetsFromConfigs()
        })
        .catch(err => {
          if (request !== this.settingsRequest) return
          this.submitError = err.response?.data?.message || 'Could not load default settings.'
          this.settingsLoading = false
        })
    },
    refreshSideBetsFromConfigs() {
      const limit = this.formValues.settings?.limit

      if (!limit || !Array.isArray(limit.sideBets) || !this.isSideBetGameType) {
        return
      }

      const apply = () => {
        const knownTypes = this.sidebetTypeOptions.map(option => Number(option.value))
        const otherSideBets = limit.sideBets.filter(sidebet => !knownTypes.includes(Number(sidebet.type)))
        const list = this.sidebetTypeOptions
          .map(option => {
            const sidebet = {
              type: null,
              key: '',
              title: '',
              enabled: true,
              order: 0,
              min: '',
              max: '',
              calculator: '',
              side: ''
            }
            this.applyTypeDefaults(sidebet, option.value)
            return sidebet
          })
          .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))

        Vue.set(limit, 'sideBets', [...otherSideBets, ...list])
      }

      if (this.$store.getters['limits/isSidebetNamesLoaded']) {
        apply()
      } else {
        Promise.resolve(this.$store.dispatch('limits/ensureLoadSidebetNames')).then(apply)
      }
    },
    prepareLabel: prepareLabel,
    normalizeSideBetSettings() {
      for (const settings of [this.formValues.settings, this.formValues.calculation?.peakSettings]) {
        const limit = settings?.limit
        if (!limit?.sideBets || Array.isArray(limit.sideBets)) continue
        const sideBets = Object.keys(limit.sideBets).map(type => {
          const sideBet = limit.sideBets[type]
          return sideBet && typeof sideBet === 'object' ? { ...sideBet, type: sideBet.type ?? Number(type) } : null
        }).filter(Boolean)
        Vue.set(limit, 'sideBets', sideBets)
      }
    },
    ensureBingoSideBet() {
      const limit = this.activeSettings?.limit
      if (!limit) return null
      if (!Array.isArray(limit.sideBets)) Vue.set(limit, 'sideBets', [])
      let sideBet = limit.sideBets.find(item => Number(item.type) === BINGO_SIDE_BET_TYPE)
      if (!sideBet) {
        sideBet = this.deepClone(BINGO_SIDE_BET_DEFAULT)
        limit.sideBets.push(sideBet)
      }
      return sideBet
    },
    setBingoEnabled(enabled) {
      if (!enabled && !this.bingoSideBet) return
      const sideBet = this.ensureBingoSideBet()
      if (sideBet) Vue.set(sideBet, 'enabled', Boolean(enabled))
      this.bingoError = ''
    },
    validateBingoLimits() {
      this.bingoError = ''
      const profiles = { normal: this.formValues.settings, peak: this.isEurSource ? this.formValues.calculation.peakSettings : null }
      for (const [profile, settings] of Object.entries(profiles)) {
        const sideBet = settings?.limit?.sideBets?.find(item => Number(item.type) === BINGO_SIDE_BET_TYPE)
        if (!sideBet || sideBet.enabled === false) continue
        const min = Number(sideBet.min)
        const max = Number(sideBet.max)
        if ([sideBet.min, sideBet.max].some(value => value === '' || value === null || value === undefined) || !Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < min) {
          this.bingoError = `${profile === 'peak' ? 'Peak' : 'Normal'} Bingo Min and Bingo Max must be non-negative numbers; Max must be greater than or equal to Min.`
          return false
        }
      }
      return true
    },
    scalarKeys(obj) {
      return Object.keys(obj).filter(
        key => key !== 'sideBets' && !Array.isArray(obj[key])
      )
    },
    typeOptionFor(type) {
      return (
        this.sidebetTypeOptions.find(option => option.value === type) ||
        (type !== null && type !== undefined
          ? { value: type, text: String(type) }
          : null)
      )
    },
    availableTypeOptions(current) {
      const usedByOthers = this.sideBets
        .filter(sidebet => sidebet !== current)
        .map(sidebet => sidebet.type)

      return this.sidebetTypeOptions.filter(
        option => !usedByOthers.includes(option.value)
      )
    },
    applyTypeDefaults(sidebet, type) {
      const def = SIDE_BET_TYPES[type] || {}
      const cfg = this.sidebetConfigs[type] || {}
      const dictName = this.sidebetTypeOptions.find(option => option.value === type)?.text

      Vue.set(sidebet, 'type', type)
      Vue.set(sidebet, 'key', cfg.key ?? def.key ?? '')
      Vue.set(sidebet, 'title', dictName ?? def.title ?? '')
      Vue.set(sidebet, 'calculator', cfg.calculator ?? def.calculator ?? '')
      Vue.set(sidebet, 'side', cfg.side ?? def.side ?? '')
      Vue.set(sidebet, 'order', cfg.order ?? def.order ?? sidebet.order ?? 0)

      if (cfg.enabled !== undefined) {
        Vue.set(sidebet, 'enabled', cfg.enabled)
      }

      if (cfg.min !== undefined) {
        Vue.set(sidebet, 'min', cfg.min)
      }
      if (cfg.max !== undefined) {
        Vue.set(sidebet, 'max', cfg.max)
      }

      const payout = cfg.payout ?? def.payout
      if (payout !== undefined && payout !== '') {
        Vue.set(sidebet, 'payout', payout)
      } else {
        Vue.delete(sidebet, 'payout')
      }
    },
    onTypeSelect(sidebet, option) {
      if (!option) {
        return
      }

      this.applyTypeDefaults(sidebet, option.value)
    },
    addSidebet() {
      const limit = this.activeSettings?.limit
      if (!limit) {
        return
      }

      if (!Array.isArray(limit.sideBets)) {
        Vue.set(limit, 'sideBets', [])
      }

      const list = limit.sideBets
      if (this.sideBets.length >= this.maxSideBets) {
        return
      }

      // A new cell starts empty — defaults are pulled in onTypeSelect once the
      // user picks a Type.
      const sidebet = {
        type: null,
        key: '',
        title: '',
        enabled: true,
        order: 0,
        min: '',
        max: '',
        calculator: '',
        side: ''
      }

      list.push(sidebet)
    },
    removeSidebet(sidebet) {
      const list = this.activeSettings?.limit?.sideBets
      if (!Array.isArray(list)) {
        return
      }

      const index = list.indexOf(sidebet)
      if (index !== -1) {
        list.splice(index, 1)
      }
    },
    onModalShow() {
      this.resetModal()
      this.$store.dispatch('limits/reloadSidebetNames')
      this.$nextTick(() => this.loadEurSources())
    },
    resetModal() {
      this.setDefaults()
      // Очищаем ошибки валидации
      this.$nextTick(() => {
        if (this.$validator) {
          this.$validator.reset()
        }
      })
      // Сбрасываем ошибки редактора настроек
      this.settingsEditorError = ''
      this.profile = 'normal'
      this.submitError = ''
      this.calculationError = ''
      this.bingoError = ''
      this.sourceRequest++
      this.settingsRequest++
      this.settingsLoading = false
    },
    hideModal() {
      this.$refs['modal-container'].hide()
    },
    toggle() {
      // При открытии модального окна сбрасываем данные к исходным значениям
      if (!this.modalShow) {
        this.setDefaults()
      }
      this.modalShow = !this.modalShow
    },
    handleSubmit() {
      if (!this.canSubmit) return
      if (!this.isCreateFromTemplate) {
        this.submitError = bonusSettingsError(this.formValues.settings) || (this.isEurSource && bonusSettingsError(this.formValues.calculation.peakSettings)) || ''
        if (this.submitError) return
      }
      if (!this.isCreateFromTemplate && !this.validateBingoLimits()) return
      this.calculationError = this.validateCalculation()
      this.submitError = ''
      if (this.calculationError) return
      if (!this.isCreateFromTemplate) {
        let value = null
        try {
          value = this.$refs['settings-editor'].editor.get()
          this.settingsEditorError = ''
        } catch {
          this.settingsEditorError = 'The Value must be a valid json string'
        }
        if (value === null) {
          this.settingsEditorError = 'Settings must be configured'
        }
      }

      this.submitting = true
      return this.$validator.validateAll().then(result => {
        if (!result || this.settingsEditorError) {
          this.submitting = false
          return
        }

        const data = {}
        // Published values are server-owned; copies carry only draft fields.
        Object.keys(this.getInitialValues()).filter(key => key !== 'published').forEach(key => {
          data[key] = this.deepClone(this.formValues[key])
        })
        if (this.create) delete data.id
        if (!this.isEurSource) {
          data.calculation.peakSettings = null
          data.calculation.peakSchedule = { timezone: 'UTC', windows: [] }
        } else {
          data.calculation.eurSourceId = null
        }
        if (this.isCreateFromTemplate) delete data.calculation
        return this.$http({
          url: this.create
            ? '/api/v1/limits'
            : '/api/v1/limits/' + this.formValues.id,
          data: {
            ...data,
            templateId:
              this.formValues.templateId?.value || this.formValues.templateId,
            type: this.formValues.type?.value || this.formValues.type,
            partnerId:
              this.formValues.partnerId?.value || this.formValues.partnerId,
            tableId: this.formValues.tableId?.value || this.formValues.tableId,
            currency:
              this.formValues.currency?.value || this.formValues.currency,
            limitType: this.formValues.limitType,
            limitName: this.formValues.limitName
          },
          method: this.create ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json'
          }
        })
          .then(() => {
            setTimeout(() => {
              this.$emit('finish')
            }, 500)

            this.hideModal()
            // Сбрасываем данные после успешного сохранения
            this.setDefaults()
            this.$toastr.i('Success')
          })
          .catch(err => {
            this.submitError = err.response?.data?.message || 'Could not save draft.'
            this.$toastr.e(this.submitError)
          })
          .finally(() => { this.submitting = false })
      })
    },
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref)
      }
      return null
    },
    setDefaults() {
      let initialValues = this.getInitialValues()

      for (let field in initialValues) {
        Vue.set(this.formValues, field, initialValues[field])
      }

      if (this.defaultValues) {
        for (let field in this.defaultValues) {
          // Глубокое клонирование для избежания мутации исходных данных
          const clonedValue = this.deepClone(this.defaultValues[field])
          Vue.set(this.formValues, field, clonedValue)
        }
      }
      const defaults = this.getInitialValues().calculation
      const calculation = this.formValues.calculation || {}
      this.formValues.calculation = {
        ...defaults,
        ...calculation,
        peakSchedule: { ...defaults.peakSchedule, ...(calculation.peakSchedule || {}) }
      }
      if (this.create) this.formValues.published = null
      this.normalizeSideBetSettings()
    },
    deepClone(obj) {
      if (obj === null || typeof obj !== 'object') {
        return obj
      }

      if (obj instanceof Date) {
        return new Date(obj.getTime())
      }

      if (Array.isArray(obj)) {
        return obj.map(item => this.deepClone(item))
      }

      const cloned = {}
      for (let key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          cloned[key] = this.deepClone(obj[key])
        }
      }

      return cloned
    }
  },
  computed: {
    isEurSource() {
      return this.formValues.currency === 'EUR'
    },
    activeSettings() {
      return this.profile === 'peak' ? this.formValues.calculation.peakSettings : this.formValues.settings
    },
    settingsFromTemplate() {
      return this.profile === 'normal' && this.isFromTemplate
    },
    sourceScopeReady() {
      return !!(this.formValues.partnerId && this.formValues.tableId && this.formValues.currency)
    },
    sourceScope() {
      return JSON.stringify([this.formValues.partnerId, this.formValues.tableId, this.formValues.currency, this.formValues.limitType, this.formValues.templateId])
    },
    missingSelectedSource() {
      return !this.isEurSource && !this.sourcesLoading && !this.sourcesError && this.formValues.calculation.eurSourceId && !this.eurSources.some(source => source.id === this.formValues.calculation.eurSourceId)
    },
    effectiveEurSource() {
      const id = this.formValues.calculation.eurSourceId
      return id ? this.eurSources.find(source => source.id === id) : this.eurSources[0]
    },
    sourcePeakSettings() {
      return this.effectiveEurSource?.calculation?.peakSettings || null
    },
    sourcePeakSchedule() {
      return this.effectiveEurSource?.calculation?.peakSchedule || null
    },
    eurSourceOptions() {
      const options = [{ value: null, text: 'Matched EUR (same table, operator, limit type)' }]
      this.eurSources.forEach(source => options.push({ value: source.id, text: `${source.limitName || 'EUR'} - ${source.id}` }))
      if (this.formValues.calculation.eurSourceId && !this.eurSources.some(source => source.id === this.formValues.calculation.eurSourceId)) options.push({ value: this.formValues.calculation.eurSourceId, text: this.formValues.calculation.eurSourceId + ' (unavailable)', disabled: true })
      return options
    },
    isCreateFromTemplate() {
      return this.create && this.formValues.templateId !== null
    },
    isFromTemplate() {
      return this.formValues.templateId !== null
    },
    canSubmit() {
      return !this.settingsLoading && !this.submitting && !this.sourcesLoading
    },
    typeOptions() {
      return (
        this.$store.getters['limits/typesForSelect']?.map(option => {
          return { value: option.value, text: option.label }
        }) || []
      )
    },
    operatorOptions() {
      return (
        this.$store.getters['limits/operatorsForSelect']?.map(option => {
          return { value: option.value, text: option.label }
        }) || []
      )
    },
    tableOptions() {
      return this.$store.getters['tablesForSelect/list'].map(opt => {
        return { value: Number.parseInt(opt.value), text: opt.label }
      })
    },
    templateOptions() {
      return this.$store.getters['limits/limitsTemplates'].map(template => {
        return { value: template.id, text: template.name }
      })
    },
    currencyOptions() {
      return (
        this.$store.getters['limits/currenciesForSelect']?.map(option => {
          return { value: option.value, text: option.label }
        }) || []
      )
    },
    selectedOperator: {
      get() {
        if (!this.formValues.partnerId) return null
        return (
          this.operatorOptions.find(
            opt => opt.value === this.formValues.partnerId
          ) || null
        )
      },
      set(value) {
        this.formValues.partnerId = value?.value || null
        this.formValues.calculation.eurSourceId = null
      }
    },
    selectedTable: {
      get() {
        if (!this.formValues.tableId) return null
        return (
          this.tableOptions.find(
            opt => opt.value === this.formValues.tableId
          ) || null
        )
      },
      set(value) {
        this.formValues.tableId = value?.value || null
        this.formValues.calculation.eurSourceId = null
      }
    },
    selectedCurrency: {
      get() {
        if (!this.formValues.currency) return null
        return (
          this.currencyOptions.find(
            opt => opt.value === this.formValues.currency
          ) || null
        )
      },
      set(value) {
        this.formValues.currency = value?.value || null
        if (!this.isEurSource) {
          this.formValues.calculation.peakSettings = null
          this.formValues.calculation.peakSchedule = { timezone: 'UTC', windows: [] }
        }
      }
    },
    selectedGameType: {
      get() {
        if (!this.formValues.type) return null
        return (
          this.typeOptions.find(opt => opt.value === this.formValues.type) ||
          null
        )
      },
      set(value) {
        this.formValues.type = value?.value || null
      }
    },
    selectedTemplate: {
      get() {
        if (!this.formValues.templateId) return null
        return (
          this.templateOptions.find(
            opt => opt.value === this.formValues.templateId
          ) || null
        )
      },
      set(value) {
        this.formValues.templateId = value?.value || null
      }
    },
    limitTypeOptions() {
      return LIMIT_TYPES
    },
    sidebetTypeOptions() {
      return this.$store.getters['limits/sidebetNames'].map(sidebet => ({
        value: sidebet.id,
        text: sidebet.name
      }))
    },
    sidebetConfigs() {
      const configs = {}
      for (const sidebet of this.$store.getters['limits/sidebetNames']) {
        if (sidebet.config) {
          configs[sidebet.id] = sidebet.config
        }
      }
      return configs
    },
    sideBets() {
      const sideBets = this.activeSettings?.limit?.sideBets ?? []
      const types = this.sidebetTypeOptions.map(option => Number(option.value))
      return Array.isArray(sideBets) ? sideBets.filter(sideBet => sideBet.type === null || types.includes(Number(sideBet.type))) : []
    },
    maxSideBets() {
      return this.sidebetTypeOptions.length
    },
    isSideBetGameType() {
      return Number(this.formValues.gameType) === SIDE_BET_GAME_TYPE
    },
    hasSideBets() {
      return this.isSideBetGameType || this.sideBets.length > 0
    },
    isRouletteGameType() {
      return isRoulette(Number(this.formValues.gameType))
    },
    bingoSideBet() {
      const sideBets = this.activeSettings?.limit?.sideBets
      return Array.isArray(sideBets) ? sideBets.find(sideBet => Number(sideBet.type) === BINGO_SIDE_BET_TYPE) || null : null
    },
    bingoEnabled() {
      return this.bingoSideBet !== null && this.bingoSideBet.enabled !== false
    },
    hasBingoLimits() {
      return this.isRouletteGameType || this.bingoSideBet !== null
    },
    selectedLimitType: {
      get() {
        if (
          this.formValues.limitType === null ||
          this.formValues.limitType === undefined
        ) {
          return { value: 0, text: 'Default' }
        }
        return (
          this.limitTypeOptions.find(
            opt => opt.value === this.formValues.limitType
          ) || { value: 0, text: 'Default' }
        )
      },
      set(value) {
        this.formValues.limitType = value?.value ?? 0
        this.formValues.limitName = value?.text || 'Default'
        this.formValues.calculation.eurSourceId = null
      }
    }
  },
  watch: {
    sourceScope() {
      this.loadEurSources()
    },
    modalShow(show) {
      if (!show) this.sourceRequest++
    },
    'formValues.type'(gameType) {
      if (gameType && (!this.defaultValues?.settings || gameType !== this.defaultValues?.type)) {
        this.formValues.calculation.peakSettings = null
        this.formValues.calculation.peakSchedule = { timezone: 'UTC', windows: [] }
        this.loadDefaultSettings(gameType)
      }
    },

    'formValues.templateId'(templateId) {
      if (this.create || !templateId) {
        return
      }

      let template = this.$store.getters['limits/limitsTemplates'].find(
        template => template.id === templateId
      )
      if (!template) {
        return
      }

      template.limits.forEach(limit => {
        if (
          limit.currency == this.formValues.currency &&
          limit.tableId == this.formValues.tableId
        ) {
          this.formValues.settings = this.deepClone(limit.settings)
          this.normalizeSideBetSettings()
          return
        }
      })
    }
  },
  beforeDestroy() {
    this.sourceRequest++
    this.settingsRequest++
  },
  async beforeMount() {
    await this.$store.dispatch('tablesForSelect/loadList')
  },
  mounted() {
    this.setDefaults()
    this.$store.dispatch('limits/ensureLoadLimitsTemplates')
    this.$store.dispatch('limits/ensureLoadTypesForSelect')
    this.$store.dispatch('limits/ensureLoadOperatorsForSelect')
    this.$store.dispatch('limits/ensureLoadCurrenciesForSelect')
    this.$store.dispatch('limits/ensureLoadSidebetNames')
  }
}
</script>

<style scoped>
.source-id { overflow-wrap: anywhere; }
</style>
